import type { ApiError } from '@empathyco/playboard-core-domain'
import type {
  PlatformToolReportingListParams,
  ToolReportDailyEntry,
  ToolReportEntry,
  ToolReportingListParams,
  ToolReportTotals,
} from '../models'
import type { ToolReportingAdapter } from './interfaces/tool-reporting.adapter'
import { injectable } from 'inversify'
import { FeatureIdEnum } from '../enums'
import { PlatformToolReportToolEnum } from '../models'

export interface PlatformToolReportEntryEntity {
  tool_name: string
  tool_id: string
  queries: Array<string>
  tooling_display_count: number
  click_count: number
  position_avg: number
  click_count_pct: number
  click_count_k: number
  click_count_k_pct: number
}

export interface PlatformToolReportTotals {
  tooling_display_count: number
  click_count: number
  click_count_k: number
  click_count_pct: number
  click_count_k_pct: number
  tools_count: number
}

export interface PlatformToolReportDaily {
  date: string
  tool_name: string
  tool_id: string
  queries: string[]
  tooling_display_count: number
  click_count: number
  click_count_k: number
  click_count_pct: number
  click_count_k_pct: number
}

export interface PlatformToolReportServiceResponse {
  data: PlatformToolReportEntryEntity[]
  params: Record<string, any>
}

export interface PlatformToolReportTotalsServiceResponse {
  data: PlatformToolReportTotals[]
  params: Record<string, any>
}

export interface PlatformToolReportDailyServiceResponse {
  data: {
    daily_data: PlatformToolReportDaily[]
  }
  params: Record<string, any>
}

export interface PlatformToolReportServiceError {
  status: number
  data: {
    code: number
    error: string
  }
}

const toolsByFeatureId: Partial<Record<FeatureIdEnum, PlatformToolReportToolEnum>> = {
  [FeatureIdEnum.PROMOTION]: PlatformToolReportToolEnum.PROMOTIONS,
}

@injectable()
export class PlatformToolReportingAdapter implements ToolReportingAdapter {
  encodeParams(params: ToolReportingListParams): PlatformToolReportingListParams {
    const tool = toolsByFeatureId[params.tool]
    if (!tool) {
      throw new Error(`Tool ${params.tool} is not supported`)
    }
    return {
      start_date: params.startDate,
      end_date: params.endDate,
      tool,
      ...(params.toolId && {
        tool_id: params.toolId,
      }),
      ...(params.sort && {
        sort_field: this.encodeSortField(params.sort.field),
        sort_order: params.sort.direction === 'ASC' ? 'ascending' : 'descending',
      }),
      ...(params.page && {
        rows: params.page.rows,
        offset: params.page.start,
      }),
    }
  }

  decode(payload: PlatformToolReportEntryEntity): ToolReportEntry {
    return {
      name: payload.tool_name,
      id: payload.tool_id,
      queries: payload.queries,
      ctr: payload.click_count_pct,
      averagePosition: payload.position_avg,
      clickCount: payload.click_count,
    }
  }

  decodeTotals(payload: PlatformToolReportTotals | undefined): ToolReportTotals {
    return {
      displayCount: payload?.tooling_display_count ?? 0,
      clickCount: payload?.click_count ?? 0,
      clickCountPct: payload?.click_count_pct ?? 0,
      toolsCount: payload?.tools_count ?? 0,
    }
  }

  decodeDaily(payload: PlatformToolReportDaily): ToolReportDailyEntry {
    return {
      date: new Date(payload.date),
      clickCount: payload.click_count,
    }
  }

  decodeError(error: PlatformToolReportServiceError): ApiError<unknown> {
    return {
      status: error.status,
      message: error.data.error,
    }
  }

  protected encodeSortField(sortField: string): string {
    const sortFields: Record<string, string> = {
      name: 'tool_name',
      ctr: 'click_count_pct',
    }

    return sortFields[sortField] ?? sortField
  }
}
